<template>
  <div id="main-nav-bar" v-if="!isHeaderHidden">
    <div class="content" v-if="appReady">
      <div class="logo-area">
        <a data-test-id="app-home-link" :class="{ logo: true, [logoClass]: true }" :href="homeURL"></a>
      </div>
      <div class="menu-area" :class="{ active: navOpen }" @click="toggleMenuWrapper" v-if="!isHeaderMenuHidden">
        <ul class="main-menu">
          <router-link :to="{ name: 'Rentals' }" tag="li" v-if="isCompanyCentralPaymentProfile && isMenuAccessible('Rentals')">
            <a data-test-id="nav-bar-link-to-rentals">
              <i class="mobile-only icon-ic-chevron-next" />
              <span>{{ $t('navigation_trips_invoices') }}</span>
            </a>
          </router-link>
          <router-link :to="{ name: 'cost-centers' }" tag="li" v-if="shouldShowCostCenterNavigation()">
            <a data-test-id="nav-bar-link-to-rentals">
              <i class="mobile-only icon-ic-chevron-next" />
              <span>{{ $t('navigation_cost_centers') }}</span>
            </a>
          </router-link>
          <router-link :to="{ name: 'Drivers' }" tag="li" v-if="isMenuAccessible('Drivers')">
            <a data-test-id="nav-bar-link-to-drivers">
              <i class="mobile-only icon-ic-chevron-next" />
              <span>{{ $t('navigation_manage_drivers') }}</span>
              <div class="notification" v-if="hasPendingRequests" />
            </a>
          </router-link>

          <li class="mobile-only">
            <router-link data-test-id="nav-bar-link-to-payments" :to="{ name: 'Payments' }" v-if="isCompanyCentralPaymentProfile && isMenuAccessible('Payments')">
              <i class="icon-ic-chevron-next" />
              <span>{{ $t('mytrips_payment_section_title') }}</span>
              <div v-if="notification.payment" class="notification" />
            </router-link>
          </li>
          <li class="mobile-only">
            <router-link data-test-id="nav-bar-link-to-account" :to="{ name: 'Account' }" v-if="isMenuAccessible('Account')">
              <i class="icon-ic-chevron-next" />
              <span>{{ $t('navigation_company') }}</span>
              <div v-if="notification.account" class="notification" />
            </router-link>
          </li>
          <li class="mobile-only" v-if="keycloakB2B.isAuthenticated">
            <router-link :to="{ name: 'edit-profile' }">
              <i class="icon-ic-edit-filled"></i>
              <span>{{ $t('edit_profile') }}</span>
            </router-link>
          </li>
          <li class="mobile-only">
            <a @click="logout" class="logout" data-test-id="nav-bar-link-to-logout">
              <span>{{ $t('global_logout') }}</span>
            </a>
          </li>
          <li class="separator" />
          <li class="full-name" @click="toggleAccountMenu">
            <a data-test-id="nav-bar-link-to-toggle-menu" data-hj-suppress>
              <span>{{ `${user.firstName} ${user.lastName}` }}</span>
              <i class="icon-ic-chevron-down toggle-menu-icon"></i>
              <div class="notification" v-if="hasNotifications" />
            </a>
            <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
              <div class="animated account-dropdown" v-if="accountMenuOpen" ref="dropdown">
                <div class="flex flex-column">
                  <ul>
                    <li>
                      <router-link data-test-id="dropdown-link-to-payments" :to="{ name: 'Payments' }" v-if="isCompanyCentralPaymentProfile && isMenuAccessible('Payments')">
                        <i class="icon-ic-payment"></i>
                        <span>{{ $t('mytrips_payment_section_title') }}</span>
                        <div v-if="notification.payment" class="notification" />
                      </router-link>
                    </li>
                    <li>
                      <router-link data-test-id="dropdown-link-to-account" :to="{ name: 'Account' }" v-if="isMenuAccessible('Account')">
                        <i class="icon-ic-home"></i>
                        <span>{{ $t('navigation_company') }}</span>
                        <div v-if="notification.account" class="notification" />
                      </router-link>
                    </li>
                    <li class="separator" />
                    <li v-if="keycloakB2B.isAuthenticated">
                      <router-link :to="{ name: 'edit-profile' }">
                        <i class="icon-ic-edit-filled"></i>
                        <span>{{ $t('edit_profile') }}</span>
                      </router-link>
                    </li>
                    <li>
                      <a @click="logout" class="logout" data-test-id="dropdown-link-to-logout">
                        <i class="icon-ic-ignition"></i>
                        <span>{{ $t('global_logout') }}</span>
                      </a>
                    </li>
                  </ul>
                  <span> v{{ appVersion }}</span>
                </div>
              </div>
            </transition>
          </li>
        </ul>
        <div class="language-selector">
          <a
            data-test-id="header-language-selector"
            v-for="(lang, index) in availableLanguages"
            :key="index"
            @click="changeLanguageTo(`${lang}-${country}`)"
            :class="{ active: language.toLowerCase() === lang }"
            >{{ lang.toUpperCase() }}</a
          >
        </div>
      </div>
      <div class="menu-icon-area" v-if="!isHeaderMenuHidden">
        <a class="menu-icon" @click="toggleMenu" data-test-id="mobile-toggle-menu">
          <i class="icon-ic-menu" v-if="!navOpen"></i>
          <i class="icon-ic-cancel" v-if="navOpen"></i>
          <div class="notification" v-if="hasNotifications && !navOpen" />
        </a>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { keycloak, keycloakB2B } from '@/services/auth/auth.service';
import { getCountryConfig } from '@/data/country.config';
import { isAccessible } from '@/utils/permissions';
import { UsersState } from '@/store/user/user.model';
import { isCostCentersEnabled } from '@/utils/feature-flags';
import { mapActions, mapGetters, mapState } from 'vuex';
import { AppState } from '@/store/app/app.store';
import { isCostCentersAvailableForCountry } from '@/utils/cost-centers';

interface Notification {
  account: boolean;
  payment: boolean;
}

export default defineComponent({
  name: 'AppHeader',

  data() {
    return {
      keycloak: keycloak,
      keycloakB2B: keycloakB2B,
      notification: {
        account: false,
        payment: false,
      } as Notification,
      accountMenuOpen: false,
    };
  },

  computed: {
    ...mapState('app', {
      isHeaderHidden: (state) => (state as AppState).isHeaderHidden,
      isHeaderMenuHidden: (state) => (state as AppState).isHeaderMenuHidden,
      navOpen: (state) => (state as AppState).navOpen,
      logoClass: (state) => (state as AppState).logoClass,
      homeURL: (state) => (state as AppState).homeURL,
      appReady: (state) => (state as AppState).appReady,
      businessModeActive: (state) => (state as AppState).businessModeActive,
    }),

    ...mapState('users', {
      isBusinessAdmin: (state) => (state as UsersState).isBusinessAdmin,
      user: (state) => (state as UsersState).user,
      roles: (state) => (state as UsersState).roles,
    }),

    ...mapState('credits', {
      minutesAllowed: (state) => state.minutesAllowed,
      creditsAllowed: (state) => state.creditsAllowed,
      onboardingStep: (state) => state.onboardingStep,
    }),

    ...mapGetters('users', ['country', 'myaccountURL', 'language']),
    ...mapGetters('companies', ['isCompanyCentralPaymentProfile']),

    showMinutes(): boolean {
      return this.minutesAllowed && !this.businessModeActive && !this.creditsAllowed;
    },

    availableLanguages() {
      if (!this.country) {
        return [];
      }

      const config = getCountryConfig(this.country, this.user.legalEntityId);
      return config.languages;
    },

    hasNotifications(): boolean {
      return this.notification.account || this.notification.payment;
    },

    hasPendingRequests(): boolean {
      return this.$store.state.driverManagement.pendingTotal > 0;
    },

    appVersion(): string {
      return process.env.VUE_APP_VERSION ?? 'undefined';
    },
  },

  methods: {
    ...mapActions('app', ['toggleMenu', 'toggleContentLoaded', 'setCustomerContext', 'loadTranslations']),

    ...mapActions('credits', ['getOnboardingStep']),

    toggleMenuWrapper(event: Event): void {
      if ((event.target! as Element).tagName !== 'DIV') {
        this.toggleMenu();
      }
    },

    isMenuAccessible(route: string): boolean {
      return isAccessible(route, this.roles);
    },

    shouldShowCostCenterNavigation(): boolean {
      return isCostCentersEnabled() && isCostCentersAvailableForCountry(this.country);
    },

    async changeLanguageTo(localeString: string): Promise<void> {
      await this.loadTranslations({ isTemp: false, localeString });
    },

    async logout(): Promise<void> {
      switch (true) {
        case this.keycloak.isAuthenticated:
          await this.keycloak.logout(this.$store);
          break;
        case this.keycloakB2B.isAuthenticated:
          await this.keycloakB2B.logout(this.$store);
          break;
      }
    },

    toggleAccountMenu(event: Event): void {
      event && event.stopPropagation();
      this.accountMenuOpen = !this.accountMenuOpen;
      if (this.accountMenuOpen) {
        document.addEventListener('click', this.toggleAccountMenu);
      } else {
        document.removeEventListener('click', this.toggleAccountMenu);
      }
    },
  },
});
</script>

<style src="./app-header.component.scss" lang="scss"></style>
